/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { KitBuilder } from "../kits/builder.js";
import { timestamp } from "../timestamp.js";
import { asRuntimeKit, callHandler } from "../index.js";
/**
 * Get all secret handlers from the given kits.
 * This is used to create a fallback list for secret asking.
 */
const secretHandlersFromKits = (kits) => {
    const secretHandlers = [];
    for (const kit of kits) {
        for (const [key, handler] of Object.entries(kit.handlers)) {
            if (key === "secrets") {
                secretHandlers.push(handler);
            }
        }
    }
    return secretHandlers;
};
export const configureSecretAsking = (interactiveSecrets, existingKits, next) => {
    if (interactiveSecrets === true) {
        return [createSecretAskingKit(next), ...existingKits];
    }
    else if (interactiveSecrets === "fallback") {
        return [
            createSecretAskingKit(next, secretHandlersFromKits(existingKits)),
            ...existingKits,
        ];
    }
    else {
        return existingKits;
    }
};
const interactiveSecretsHandler = (next) => {
    return async (inputs) => {
        const { keys } = inputs;
        if (!keys)
            return {};
        let outputs = {};
        await next({
            type: "secret",
            data: { keys, timestamp: timestamp() },
            reply: async (value) => {
                outputs = value.inputs;
            },
        });
        return outputs;
    };
};
const fallbackHandler = (nodeType, handlers, interactive) => {
    const handler = async (inputs, context) => {
        const { keys } = inputs;
        if (!keys)
            return {};
        // OAuth keys can only be handled interactively.
        const connectionKey = keys.length === 1 && keys[0].startsWith("connection:");
        const adjustedHandlers = connectionKey
            ? [interactive]
            : [...handlers, interactive];
        for (const handler of adjustedHandlers) {
            const outputs = await callHandler(handler, inputs, context);
            if (outputs && !outputs["$error"]) {
                return outputs;
            }
        }
        throw new Error(`No handler found for type "${nodeType}"`);
    };
    return handler;
};
export const createSecretAskingKit = (next, fallback) => {
    const interactive = interactiveSecretsHandler(next);
    const secrets = fallback
        ? fallbackHandler("secrets", fallback, interactive)
        : interactive;
    const secretAskingKit = new KitBuilder({
        url: "secret-asking-kit",
    }).build({ secrets });
    return asRuntimeKit(secretAskingKit);
};
//# sourceMappingURL=secrets.js.map